import React, { useState, useEffect } from "react";
import learningPathData from "../../_library/learningpath.json";
import Header from "../../layouts/Header";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import BeforeLoginModal from "../../components/BeforeLoginModal";
import CourseDetailsModal from "../../components/CourseDetailsModal";
import {
  faArrowRight,
  faClockFour,
  faFileVideo,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import Tooltip from "react-tooltip-lite";
import jsonData from "../../_library/courses.json";

const staticDevelopers = learningPathData.roles;

// List of images for dynamic paths (add more as needed)
const dynamicPathImages = [
  "./images/learningpath/L1.png",
  "./images/learningpath/L2.png",
  "./images/learningpath/L3.png",
  "./images/learningpath/L4.png",
  "./images/learningpath/L5.png",
  "./images/learningpath/L6.png",
];

const LearningPath = ({
  isLoggedIn,
  addToCart,
  removeFromCart,
  cartItemCount,
  isUSDCurrency,
  toggleCurrency,
  getCartItems,
}) => {
  const [selectedSection, setSelectedSection] = useState(null);
  const [showBeforeLoginModal, setShowBeforeLoginModal] = useState(false);
  const [show, setShow] = useState(false);
  const [item, setItem] = useState(null);
  const [allCourses, setAllCourses] = useState([]);
  const [dynamicPaths, setDynamicPaths] = useState([]);

  const discountInrCost = 600;
  const discountedCost = 8;

  useEffect(() => {
    const loadCourses = async () => {
      const data = await jsonData;
      const courses = data?.CoursesCollection || [];
      setAllCourses(courses);

      // Generate dynamic paths grouped by CourseCategory
      const grouped = {};
      courses.forEach((course) => {
        const cat = course.CourseCategory?.trim() || "Other";
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push(course);
      });

      const dynamic = Object.entries(grouped).map(([category, coursesList], idx) => ({
        label: `${category} Developer Path`,
        isDynamic: true,
        details: {
          subtitle: `Master modern ${category.toLowerCase()} development`,
          cardImage: dynamicPathImages[idx % dynamicPathImages.length],
          roadmapContent: { 
            courses: coursesList.slice(0, 15) // Limit for better UX
          },
        },
      }));

      setDynamicPaths(dynamic);
    };

    loadCourses();
  }, []);

  const allPaths = [...staticDevelopers, ...dynamicPaths];

  const handleSectionClick = (section) => {
    setSelectedSection(section);
  };

  const handleBackClick = () => {
    setSelectedSection(null);
  };

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const fetchCourseById = (courseId) => {
    return allCourses.find((c) => String(c.CourseId) === String(courseId)) || null;
  };

  const tokenize = (text = "") =>
    text
      .toLowerCase()
      .replace(/<[^>]*>/g, " ")
      .replace(/[^a-z0-9 ]/g, " ")
      .split(/\s+/)
      .filter((x) => x.length > 2);

  const getRecommendedCourses = (excludeCourses = []) => {
    const existingIds = new Set(excludeCourses.map((x) => Number(x.CourseId)));
    const keywords = new Set();

    excludeCourses.forEach((course) => {
      tokenize(course.CourseTitle).forEach((x) => keywords.add(x));
      tokenize(course.courseDescription || course.CourseDescription).forEach((x) => keywords.add(x));
    });

    return allCourses
      .filter((course) => !existingIds.has(Number(course.CourseId)))
      .map((course) => {
        let score = 0;
        [
          ...tokenize(course.CourseTitle),
          ...tokenize(course.CourseCategory || ""),
          ...tokenize(course.CourseDescription || ""),
        ].forEach((word) => {
          if (keywords.has(word)) score++;
        });
        return { ...course, score };
      })
      .filter((x) => x.score > 2) // Higher threshold for quality
      .sort((a, b) => b.score - a.score)
      .slice(0, 8);
  };

  const recommendedCourses = getRecommendedCourses([]);

  const renderSections = () => {
    if (selectedSection) {
      const section = selectedSection;
      const courses = section.details.roadmapContent.courses || [];

      return (
        <div className="container fwc">
          <div className="row">
            <div className="col-1">
              <button className="lpbb mt-3" onClick={handleBackClick}>
                <img className="wi40" src="./images/back-icon.svg" alt="Back to All Paths" />
              </button>
            </div>
            <div className="col-10">
              <h2 className="section-head">{section.label} Roadmap</h2>
            </div>
          </div>

          <div className="row">
            <div className="col">
              <div className="roadmap-main">
                <div className="row roadmap-box">
                  {courses.map((course, index) => (
                    <div className="col-lg-4 col-md-6 col-12" key={index}>
                      <div className="containerr">
                        <div className="card">
                          <div className="face face1">
                            <div className="content">
                              <img
                                src={course.CourseImageURL100x100}
                                alt={course.CourseTitle}
                              />
                              <p className="course-title">{course.CourseTitle}</p>
                            </div>
                          </div>
                          <div className="face face2">
                            <div className="content">
                              <p>{course.courseDescription || course.CourseDescription}</p>
                              <div className="feature-info rodmp">
                                <div className="lessons">
                                  <FontAwesomeIcon icon={faFileVideo} />{" "}
                                  <span>{course.lessons || "N/A"} Lessons</span>
                                </div>
                                <div className="hours">
                                  <FontAwesomeIcon icon={faClockFour} />{" "}
                                  <span>{course.hours || "N/A"}</span>
                                </div>
                              </div>
                              <div className="rbbox">
                                <div className="buttons">
                                  <Tooltip content="Download this course" tagName="button">
                                    <button
                                      className="buy lp"
                                      onClick={() =>
                                        isLoggedIn
                                          ? addToCart("Download", course, isUSDCurrency ? discountedCost : discountInrCost)
                                          : setShowBeforeLoginModal(true)
                                      }
                                    >
                                      <span>{isUSDCurrency ? `${discountedCost}$` : `${discountInrCost}₹`}</span>
                                      <FontAwesomeIcon icon={faDownload} />
                                    </button>
                                  </Tooltip>

                                  <button
                                    className="details-btn"
                                    onClick={() => {
                                      const courseData = fetchCourseById(course.CourseId);
                                      setItem(courseData);
                                      handleShow();
                                    }}
                                  >
                                    <span>Details</span>
                                    <i><FontAwesomeIcon icon={faArrowRight} /></i>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Main Page
    return (
      <div className="container">
        <h2 className="section-head">
          Your Comprehensive <br /> Learning Path to Tech Excellence
        </h2>
        <p className="text-center">
          Explore, Learn, and Excel in the World of Technology with our Specialized Learning Tracks
        </p>

        {/* Static Learning Paths */}
        <div className="row g-4 pb-4 custom-row">
          {staticDevelopers.map((dev, index) => (
            <div className="col-lg-4 col-md-6 col-12" key={index}>
              <div
                className={`lpath__card box${(index % 6) + 1}`}
                onClick={() => handleSectionClick(dev)}
              >
                <div className="icr-box">
                  <img src={dev.details.cardImage} alt={dev.label} />
                </div>
                <div className="ccbx">
                  <h4 className="mb-3 mt-4">{dev.label}</h4>
                  <h6 className="mb-2 bold">{dev.details.subtitle}</h6>
                  <button className="btn primary mw200">Start Learning</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Dynamic Learning Paths */}
        {dynamicPaths.length > 0 && (
          <>
            <h3 className="section-head mt-5">More Learning Paths</h3>
            <div className="row g-4 pb-4 custom-row">
              {dynamicPaths.map((path, index) => (
                <div className="col-lg-4 col-md-6 col-12" key={`dyn-${index}`}>
                  <div
                    className={`lpath__card box${((index + staticDevelopers.length) % 6) + 1}`}
                    onClick={() => handleSectionClick(path)}
                  >
                    <div className="icr-box">
                      <img src={path.details.cardImage} alt={path.label} />
                    </div>
                    <div className="ccbx">
                      <h4 className="mb-3 mt-4">{path.label}</h4>
                      <h6 className="mb-2 bold">{path.details.subtitle}</h6>
                      <button className="btn primary mw200">Explore Path</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Recommended Courses - Conditional */}
        {recommendedCourses.length > 0 && (
          <div className="mt-5">
            <h3 className="section-head">Recommended Courses for You</h3>
            <div className="row g-4">
              {recommendedCourses.map((course) => (
                <div className="col-lg-4 col-md-6 col-12" key={course.CourseId}>
                  <div
                    className="containerr cursor-pointer"
                    onClick={() => {
                      const fullCourse = fetchCourseById(course.CourseId);
                      setItem(fullCourse);
                      handleShow();
                    }}
                  >
                    <img 
                      src={course.CourseImageURL100x100} 
                      alt={course.CourseTitle}
                      style={{ width: "100%", borderRadius: "8px" }}
                    />
                    <h5 className="mt-3">{course.CourseTitle}</h5>
                    <small>{course.CourseCategory}</small>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="homepage-wrapper">
      <img src="./images/giphy-new.gif" id="background-video" alt="" />
      <Header
        style={{ position: "relative", borderBottom: "1px solid rgba(255,255,255,0.8)" }}
        removeFromCart={removeFromCart}
        getCartItems={getCartItems}
        cartItemCount={cartItemCount}
        isUSDCurrency={isUSDCurrency}
        toggleCurrency={toggleCurrency}
      />

      <main>
        <div className="top-box"></div>
        <section className="lpath pb-115">{renderSections()}</section>
      </main>

      {show && item && (
        <CourseDetailsModal
          courseData={item}
          show={show}
          handleClose={handleClose}
          isUSDCurrency={isUSDCurrency}
          addToCart={addToCart}
          isLoggedIn={isLoggedIn}
        />
      )}

      {showBeforeLoginModal && (
        <BeforeLoginModal handleClose={() => setShowBeforeLoginModal(false)} />
      )}
    </div>
  );
};

export default LearningPath;